<template>
  <transition name="fade">
    <div v-if="uiStore.isLoading" class="loading-overlay" :class="{ 'loading-overlay--scoped': scoped }">
      <div class="loading-content">
        <div class="spinner-container">
          <div class="spinner-ring"></div>
          <div class="spinner-core"></div>
        </div>
        <div class="loading-text" v-if="loadingText">{{ loadingText }}</div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useUIStore } from '@/store/ui.store'

export default defineComponent({
  name: 'LoadingOverlay',
  props: {
    scoped: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const uiStore = useUIStore()
    const loadingText = computed(() => uiStore.loadingText)

    return {
      uiStore,
      loadingText
    }
  }
})
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--ui-inverse-overlay);
  backdrop-filter: blur(6px);
  z-index: 10000; /* High z-index to cover everything */
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Scoped variant — fills its containing element only.
   Used inside AppShell so the rail + top-bar stay visible during section loads. */
.loading-overlay--scoped {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 50;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem 2.25rem;
  border-radius: 1.5rem;
  background: linear-gradient(165deg, var(--ui-inverse-surface) 0%, var(--ui-inverse-surface-soft) 100%);
  border: 1px solid var(--ui-inverse-border-strong);
  box-shadow: 0 1.25rem 2.75rem rgba(0, 10, 25, 0.36);
}

.spinner-container {
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: var(--ui-inverse-text);
  border-right-color: var(--ui-inverse-text);
  border-radius: 50%;
  animation: spin 1.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite;
}

.spinner-core {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40%;
  height: 40%;
  background: var(--ui-inverse-cta-bg);
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

.loading-text {
  font-family: var(--ui-inverse-font-family);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--ui-inverse-text);
  letter-spacing: 0.05em;
  animation: fade-in-up 0.5s ease-out;
  text-transform: none;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; box-shadow: 0 0 20px rgba(16, 26, 52, 0.35); }
  100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
}

@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Vue Transition Styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
